import { ArrowUpRight, ChevronDown } from "lucide-react";
import StarField from "./StarField";
import { CONTACT_URL } from "../content";
import { track } from "../lib/analytics";

export default function Hero({ t }) {
  const handleContactClick = () => {
    track("contact_click");
    track("book_demo");
  };

  const heroImg =
    "https://images.unsplash.com/photo-1768329051109-9986d6926566?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxzdGFyJTIwZmllbGQlMjBjb3Ntb3N8ZW58MHx8fHwxNzgxNjE2NjAzfDA&ixlib=rb-4.1.0&q=85";

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden"
    >
      {/* Background layers */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${heroImg})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#030508]/90 via-[#030508]/70 to-[#030508]"
        aria-hidden="true"
      />
      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(255,184,0,0.08), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <StarField count={70} />

      {/* Orbital ring */}
      <div
        className="absolute -right-40 top-20 hidden lg:block"
        aria-hidden="true"
      >
        <div className="relative h-[600px] w-[600px] orbit-slow">
          <div className="absolute inset-0 border border-white/5 rounded-full" />
          <div className="absolute inset-8 border border-white/5 rounded-full" />
          <div className="absolute inset-20 border border-[var(--primary)]/15 rounded-full" />
          <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[var(--primary)] shadow-[0_0_20px_var(--primary)]" />
        </div>
      </div>

      {/* Crosshair markings */}
      <div className="absolute top-24 left-6 md:left-10 font-mono text-[10px] text-white/30 tracking-[0.3em]">
        N 40°24′ · W 03°41′
      </div>
      <div className="absolute top-24 right-6 md:right-10 font-mono text-[10px] text-white/30 tracking-[0.3em] flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--cyan)] animate-pulse" />
        LIVE TELEMETRY
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <div className="max-w-4xl">
          {/* Kicker */}
          <div
            className="rise rise-1 inline-flex items-center gap-3 mb-8"
            data-testid="hero-kicker"
          >
            <span className="h-px w-10 bg-[var(--primary)]" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-[var(--primary)]">
              {t.hero.kicker}
            </span>
          </div>

          {/* Title */}
          <h1
            data-testid="hero-title"
            className="font-display font-black uppercase leading-[0.95] tracking-tight"
          >
            <span className="rise rise-2 block text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white">
              {t.hero.title1}
            </span>
            <span className="rise rise-3 block text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-2">
              <span className="text-white/90">{t.hero.title2.split(" ")[0]} </span>
              <span className="text-[var(--primary)]">
                {t.hero.title2.split(" ").slice(1).join(" ")}
              </span>
              <span className="cursor-blink text-[var(--primary)] ml-1">_</span>
            </span>
          </h1>

          {/* Subtitle */}
          <p
            data-testid="hero-subtitle"
            className="rise rise-4 mt-8 max-w-2xl text-base md:text-lg text-white/65 font-light leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          {/* CTAs */}
          <div
            className="rise rise-5 mt-10 flex flex-wrap items-center gap-4"
            data-testid="hero-ctas"
          >
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              onClick={handleContactClick}
              className="group inline-flex items-center gap-3 bg-[var(--primary)] text-black font-mono text-xs tracking-[0.25em] uppercase font-semibold px-7 py-4 hover:bg-[var(--primary-hover)] transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#plans"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-3 border border-white/20 text-white font-mono text-xs tracking-[0.25em] uppercase font-semibold px-7 py-4 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
            >
              {t.hero.ctaSecondary}
              <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </div>

          {/* Stats strip */}
          <div
            data-testid="hero-stats"
            className="rise rise-5 mt-16 grid grid-cols-3 gap-4 max-w-2xl border-t border-white/10 pt-8"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="">
                <div className="font-display text-3xl md:text-4xl font-bold text-white">
                  {s.value}
                </div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marquee ticker */}
      <div
        data-testid="hero-marquee"
        className="absolute bottom-0 left-0 right-0 border-t border-b border-white/10 bg-[#030508]/80 backdrop-blur-sm overflow-hidden"
      >
        <div className="flex whitespace-nowrap marquee-track py-3">
          {[...t.hero.tickers, ...t.hero.tickers].map((item, i) => (
            <span
              key={i}
              className="font-mono text-[11px] tracking-[0.35em] uppercase text-white/50 mx-8 flex items-center gap-8"
            >
              <span className="text-[var(--primary)]">◆</span>
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
